import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import roleManager, { PERMISSIONS } from '../utils/roleManager';
import { getLeadUrgency } from '../utils/leadUrgency';
import { apiClient, ApiError } from '../utils/apiClient';
import './LeadDetail.css';
import { confirmDialog, alertDialog } from '../utils/dialogs';

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'proposal_sent', 'converted', 'lost'];
const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  proposal_sent: 'Proposal Sent', converted: 'Converted', lost: 'Lost'
};
const PRIORITY_OPTIONS = ['low', 'medium', 'high'];
const ACTIVITY_TYPE_OPTIONS = ['note', 'call', 'email', 'meeting'];
const ACTIVITY_TYPE_LABELS = { note: 'Note', call: 'Call', email: 'Email', meeting: 'Meeting', status_change: 'Status Change' };
const ACTIVITY_TYPE_ICONS = { note: 'fa-note-sticky', call: 'fa-phone', email: 'fa-envelope', meeting: 'fa-people-group', status_change: 'fa-arrow-right-arrow-left' };

function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activityType, setActivityType] = useState('note');
  const [activityDescription, setActivityDescription] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');
  const [logging, setLogging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);

  const canAssign = roleManager.hasPermission(PERMISSIONS.ASSIGN_LEADS);
  const canConvert = roleManager.hasPermission(PERMISSIONS.CONVERT_LEADS);
  const canDelete = roleManager.hasPermission(PERMISSIONS.DELETE_LEADS);

  const fetchLead = useCallback(async () => {
    try {
      const data = await apiClient.get(`/api/leads/${leadId}`);
      setLead(data);
      setNextFollowUp(data.next_follow_up_date || '');
    } catch (err) {
      console.error('Error fetching lead:', err);
      setError(err instanceof ApiError ? 'Lead not found' : 'Error fetching lead');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  const fetchActivities = useCallback(async () => {
    try {
      setActivities(await apiClient.get(`/api/leads/${leadId}/activities`));
    } catch (err) {
      console.error('Error fetching lead activities:', err);
    }
  }, [leadId]);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }
    fetchLead();
    fetchActivities();

    apiClient.post('/api/users/findall')
      .then((data) => setStaff(Array.isArray(data) ? data.filter((u) => (u.role_type || '').toLowerCase() !== 'customer') : []))
      .catch(() => setStaff([]));
  }, [navigate, fetchLead, fetchActivities]);

  const updateLead = async (patch) => {
    try {
      await apiClient.put(`/api/leads/${leadId}`, patch);
      fetchLead();
      fetchActivities();
    } catch (err) {
      console.error('Error updating lead:', err);
    }
  };

  const submitActivity = async (e) => {
    e.preventDefault();
    if (!activityDescription.trim()) return;
    setLogging(true);
    try {
      await apiClient.post(`/api/leads/${leadId}/activities`, {
        activity_type: activityType,
        description: activityDescription,
        next_follow_up_date: nextFollowUp || null
      });
      setActivityDescription('');
      fetchLead();
      fetchActivities();
    } catch (err) {
      console.error('Error logging activity:', err);
      alertDialog('Failed to log activity');
    } finally {
      setLogging(false);
    }
  };

  const deleteLead = async () => {
    if (!await confirmDialog(`Permanently delete this lead? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/leads/${leadId}`);
      navigate('/leads');
    } catch (err) {
      console.error('Error deleting lead:', err);
      alertDialog('Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const convertLead = async () => {
    if (!await confirmDialog('Convert this lead into a customer record?')) return;
    setConverting(true);
    try {
      const data = await apiClient.post(`/api/leads/${leadId}/convert`);
      fetchLead();
      if (await confirmDialog('Lead converted. View the new customer record now?')) {
        navigate(`/customer/${data.customer.id}`);
      }
    } catch (err) {
      console.error('Error converting lead:', err);
      alertDialog(err.message || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const getInitials = (name) => name ? name.split(' ').map((n) => n[0]).join('').toUpperCase().substring(0, 2) : 'NA';

  if (loading) return <PageShell><div className="lead-loading">Loading lead...</div></PageShell>;
  if (error || !lead) {
    return (
      <PageShell>
        <div className="lead-error">{error || 'Lead not found'}</div>
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
      </PageShell>
    );
  }

  const urgency = getLeadUrgency(lead);

  return (
    <PageShell>
      <div className="lead-detail-container">
        <div className="lead-detail-header">
          <div className="lead-header-left">
            <button onClick={() => navigate('/leads')} className="lead-back-btn">
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <h1>{lead.company_name || 'Unnamed Lead'}</h1>
          </div>
          <div className="lead-header-right">
            <select
              className={`lead-status-select lead-status-badge lead-status-${lead.status}`}
              value={lead.status}
              disabled={lead.status === 'converted'}
              onChange={(e) => updateLead({ status: e.target.value })}
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
            <select
              className={`lead-priority-select lead-priority-badge lead-priority-${lead.priority}`}
              value={lead.priority}
              onChange={(e) => updateLead({ priority: e.target.value })}
            >
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {canConvert && lead.status !== 'converted' && (
              <Button variant="secondary" onClick={convertLead} disabled={converting}>
                {converting ? 'Converting...' : 'Convert'}
              </Button>
            )}
            {canDelete && (
              <Button variant="danger" onClick={deleteLead} disabled={deleting}>
                <i className="fas fa-trash"></i> {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </div>
        </div>

        <div className={`lead-urgency-banner lead-urgency-${urgency.key}`}>
          <i className="fas fa-clock"></i>
          {urgency.key === 'none' ? 'No follow-up date set - log an activity below to schedule one.' : (
            <>Next follow-up: <strong>{lead.next_follow_up_date}</strong> ({urgency.label})</>
          )}
        </div>

        <div className="lead-info-grid">
          <div className="lead-main-info">
            <div className="lead-info-card">
              <h3>Contact</h3>
              <div className="lead-meta">
                <div className="lead-meta-item"><i className="fas fa-user"></i><span>{lead.contact_first_name} {lead.contact_last_name}</span></div>
                <div className="lead-meta-item"><i className="fas fa-envelope"></i><span>{lead.email || 'No email'}</span></div>
                <div className="lead-meta-item"><i className="fas fa-phone"></i><span>{lead.phone || 'No phone'}</span></div>
                <div className="lead-meta-item"><i className="fas fa-bullhorn"></i><span>Source: {lead.source || 'Unknown'}</span></div>
                <div className="lead-meta-item"><i className="fas fa-sterling-sign"></i><span>Est. Value: {lead.estimated_value ? `£${parseFloat(lead.estimated_value).toFixed(2)}` : 'Not set'}</span></div>
                <div className="lead-meta-item"><i className="fas fa-calendar"></i><span>Created: {formatDateTime(lead.createdAt)}</span></div>
                <div className="lead-meta-item"><i className="fas fa-comment"></i><span>Last Contacted: {formatDateTime(lead.last_contacted_at)}</span></div>
              </div>
              {lead.notes && (
                <div className="lead-notes">
                  <h4>Notes</h4>
                  <p>{lead.notes}</p>
                </div>
              )}
            </div>
          </div>
          <div className="lead-sidebar-info">
            <div className="lead-info-card">
              <h3>Assignment</h3>
              <FormField id="lead-assigned-to" label="Assigned To">
                <select
                  id="lead-assigned-to"
                  value={lead.assigned_to_user_id || ''}
                  disabled={!canAssign}
                  onChange={(e) => updateLead({ assigned_to_user_id: e.target.value || null })}
                >
                  <option value="">Unassigned</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </FormField>
            </div>
          </div>
        </div>

        <div className="lead-log-section lead-info-card">
          <h3>Log Activity</h3>
          <form onSubmit={submitActivity}>
            <div className="lead-log-row">
              <FormField id="activity-type" label="Type">
                <select id="activity-type" value={activityType} onChange={(e) => setActivityType(e.target.value)}>
                  {ACTIVITY_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>)}
                </select>
              </FormField>
              <FormField id="next-follow-up" label="Next Follow-Up Date">
                <input id="next-follow-up" type="date" value={nextFollowUp} onChange={(e) => setNextFollowUp(e.target.value)} />
              </FormField>
            </div>
            <FormField id="activity-description" label="What happened?">
              <textarea
                id="activity-description"
                rows={3}
                placeholder="e.g. Called to discuss pricing, following up next week..."
                value={activityDescription}
                onChange={(e) => setActivityDescription(e.target.value)}
              />
            </FormField>
            <Button type="submit" disabled={logging || !activityDescription.trim()}>
              {logging ? 'Saving...' : 'Log Activity'}
            </Button>
          </form>
        </div>

        <div className="lead-activities-section">
          <div className="lead-section-header">
            <h2><i className="fas fa-history"></i> Activity Timeline</h2>
          </div>
          <div className="lead-activities-container">
            {activities.length === 0 ? (
              <div className="lead-no-activities"><i className="fas fa-inbox"></i><p>No activity logged yet.</p></div>
            ) : (
              <div className="lead-activities-timeline">
                {activities.map((activity) => (
                  <div key={activity.id} className={`lead-activity-item ${activity.activity_type === 'status_change' ? 'lead-activity-status-change' : ''}`}>
                    <div className="lead-activity-avatar">
                      {activity.activity_type === 'status_change' ? <i className={`fas ${ACTIVITY_TYPE_ICONS.status_change}`}></i> : getInitials(activity.author_name)}
                    </div>
                    <div className="lead-activity-content">
                      <div className="lead-activity-header">
                        <div className="lead-activity-author">
                          <strong>{activity.author_name || 'Unknown'}</strong>
                          <span className="lead-activity-type"><i className={`fas ${ACTIVITY_TYPE_ICONS[activity.activity_type]}`}></i> {ACTIVITY_TYPE_LABELS[activity.activity_type]}</span>
                        </div>
                        <div className="lead-activity-date">{formatDateTime(activity.createdAt)}</div>
                      </div>
                      <div className="lead-activity-description">{activity.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

export default LeadDetail;
